import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { AttachmentWorkItem } from 'src/app/business/attachment/attachment-work-item';
import { IDispatcher } from 'src/app/common/contracts/dispatch/dispatcher';
import { Dispatcher } from 'src/app/common/dispatch/dispatcher';
import { Logger } from 'src/app/common/logging/logger';
import { AttachmentDto } from 'src/app/shared/models/attachment/attachment-dto.model';
import { AttachmentMetadata } from 'src/app/shared/models/attachment/attachment-meta-data.model';
import { AttachmentDatabaseService } from 'src/app/shared/services/attachment/attachment-database.service';
import { AttachmentQueuesProcessorService } from 'src/app/shared/services/attachment/attachment-queues-processor.service';
import {
  AttachmentTransferService,
  OfflineUploadResult,
  UploadResult,
} from 'src/app/shared/services/attachment/attachment-transfer.service';
import { Database } from 'src/app/shared/services/attachment/Database';

import { AttachmentModelName } from '../../models/model-names';
import { SessionController } from 'src/app/auth/session/session-controller';

import { AccountService } from '../account.service';
import { ConnectionStateService } from '../connection-state.service';
import { ATTACHMENT_LOCAL_PREFIX, IAttachmentService } from './attachment-service.interface';
import { AttachmentUtilsService } from './attachment-utils.service';
export type UploadReadyMetadata = Omit<AttachmentMetadata & { uniqueId: string }, 'name'>;
/** name: Temporary filename used only to extract the file extension; removed before upload.
 * uniqueId: optional pre-generated stable id (the woundMeasurement entity references the wound
 * image through it, see {@link IWoundMeasurement.attachmentUniqueId})*/
export type CreateMetadata = Partial<AttachmentMetadata> & { name: string; uniqueId?: string };
type CreateObjectUrlType = (object: any) => string;

export const CreateObjectUrl = new InjectionToken<CreateObjectUrlType>('createObjectUrl', {
  providedIn: 'root',
  factory: () => URL.createObjectURL,
});

/**
 * Native (Capacitor/Cordova) implementation of {@link IAttachmentService}.
 * Only injected on native platforms (see `AttachmentServiceToken` provider in `app.module.ts`);
 * web uses `WebAttachmentService`.
 *
 * Storage model: attachment *metadata* lives in the local `attachment.db` (filled by the sync
 * pipeline, see `AttachmentSyncService`), *binaries* live in the local chunked blob store
 * (`attachmentChunks`, see `AttachmentDatabaseService`). Binaries are downloaded on demand and
 * persisted locally (write-through in `AttachmentTransferService.fetchBlob`). Offline mutations
 * are parked in queue stores and flushed by {@link runAttachmentQueuesProcessor} once online.
 */
@Injectable({ providedIn: 'root' })
export class AttachmentService implements IAttachmentService {
  static TAG = 'AttachmentService';

  private _websocketConnectedSubscription = new Subscription();
  private _isBusyWithOfflineUpload: boolean;

  public get offlineTxSuccess(): Observable<OfflineUploadResult> {
    return this._attachmentTransferService.offlineTxSuccess;
  }

  constructor(
    private _accountService: AccountService,
    private readonly _session: SessionController,
    private _platform: Platform,
    private _connectionStateService: ConnectionStateService,
    private _attachmentDbProvider: AttachmentDatabaseService,
    private _attachmentTransferService: AttachmentTransferService,
    private _attachmentQueuesProcessor: AttachmentQueuesProcessorService,
    private _attachmentUtils: AttachmentUtilsService,
    private _logger: Logger,
    private _attachmentWorkItem: AttachmentWorkItem,
    @Inject(Dispatcher) private _dispatcher: IDispatcher<AttachmentDto>
  ) {}

  public async init() {
    this._logger.info('[AttachmentService] Initializing service');
    this._platform.pause.subscribe(_ => {
      if (this._websocketConnectedSubscription && !this._websocketConnectedSubscription.closed) {
        this._websocketConnectedSubscription.unsubscribe();
      }
    });
    this._platform.resume.subscribe(_ => this.registerOnlineObservable());
    this.registerOnlineObservable();
    this.performDelete = this.performDelete.bind(this);
    this.uploadLocal = this.uploadLocal.bind(this);
  }

  /**
   * Effectively a no-op in production: this class is only injected on native platforms,
   * where `platform.is('hybrid')` is always true and the method early-returns. The body
   * below only runs in non-hybrid dev setups. Metadata persistence on device is handled
   * by the sync pipeline, not by this method.
   */
  public async save(attachments: AttachmentDto[]) {
    await this._platform.ready();
    if (this._platform.is('hybrid') || !attachments || !attachments.length) {
      return;
    }

    await Promise.all(attachments.map(attachment => this._attachmentWorkItem.create(attachment)));

    this._dispatcher.sync(AttachmentModelName, {
      created: attachments,
      updated: [],
      deleted: [],
    });
  }

  /**
   * Returns the binary of an attachment, local-first:
   * 1. Local blob store ({@link getLocalBlob}). Hit → no network involved.
   * 2. Miss → reads the metadata from the local `attachment.db` (for the content type) and
   *    downloads the blob from the server; the download persists the blob locally
   *    (write-through in `AttachmentTransferService.fetchBlob`), so the next call is a local hit.
   *
   * Failure modes:
   * - Blob missing locally and id has the `local|` prefix (created on this device, never
   *   uploaded): throws immediately — the binary is gone and cannot be re-downloaded.
   * - Blob missing locally and *metadata* missing in the local db: throws a `TypeError`
   *   (`metadata` resolves `undefined`, see note on {@link getAttachmentDto}).
   * - Blob missing locally and offline: the feathers call rejects (~5s socket timeout)
   *   or, with a half-open connection, may hang (callers guard with their own timeout).
   *
   * @param returnValue Desired representation: `'blob'`, `'base64'` or `'blobUrl'`.
   */
  public async get<T extends 'blobUrl' | 'blob' | 'base64'>(
    id: string,
    returnValue: T
  ): Promise<T extends 'blob' ? Blob : string> {
    let blob = await this.getLocalBlob(id);
    if (!blob) {
      const metadata = await this.getAttachmentDto(id);
      blob = await this.download(id, metadata.contentType);
    }

    return this._attachmentUtils.transformBlob(blob, returnValue) as Promise<T extends 'blob' ? Blob : string>;
  }

  /**
   * Reads the binary from the local blob store.
   *
   * @returns The blob, or `undefined` when not stored locally (caller falls back to download).
   * @throws When the blob is missing for a `local|`-prefixed id: such an attachment was never
   *         uploaded, so there is no server copy to fall back to — fail fast instead.
   */
  private async getLocalBlob(id: string) {
    const blob = (await this._attachmentDbProvider.get(Database.Blob, id)) as Blob;
    if (!blob && id.startsWith(ATTACHMENT_LOCAL_PREFIX)) {
      throw Error(`blob is missing for ${id}`);
    }

    return blob;
  }

  /**
   * Downloads the binary via `AttachmentTransferService.fetchBlob`, which checks the local
   * blob store first and *persists* a downloaded blob locally (write-through). Used both
   * on demand (cache miss in {@link get}) and by the sync pipeline to prefetch swodoc
   * attachments of the user's own patients (`AttachmentSyncService`).
   *
   * Rejects when offline / the attachment does not exist on the server.
   */
  public async download(id: string, mimeType: string): Promise<Blob> {
    await this._accountService.accountReady;
    const blob = await this._attachmentTransferService.fetchBlob(id, mimeType);
    return blob;
  }

  public async create(base64String: string, metadata: CreateMetadata): Promise<UploadResult> {
    this._logger.addBreadcrumb({
      message: `${AttachmentService.TAG} addAttachment`,
      type: 'debug',
      data: {
        base64size: base64String?.length,
        metadata: metadata,
      },
    });

    if (!base64String) {
      return Promise.reject('no image data');
    }

    const { blob, uploadMetadata, filename, mime } = await this._attachmentUtils.prepareAttachmentData(
      base64String,
      metadata
    );

    this._logger.addBreadcrumb({
      message: `${AttachmentService.TAG} addAttachment writeBlobToStorage`,
      type: 'debug',
      data: {
        blobSize: blob?.size,
      },
    });

    // Writing to storage
    await this._attachmentDbProvider.writeBlobToStorage(uploadMetadata.uniqueId, blob);
    this._logger.addBreadcrumb({
      message: `${AttachmentService.TAG} addAttachment writeAttachmentToStorage`,
      type: 'debug',
    });
    await this._attachmentDbProvider.writeAttachmentToStorage({
      _id: uploadMetadata.uniqueId,
      filename,
      contentType: mime,
      mime,
      uploadDate: new Date(new Date().toUTCString()),
      metadata: {
        ...uploadMetadata,
        createdAt: new Date(new Date().toUTCString()),
        createdBy: await this._accountService.getCurrentUserId(),
      },
    });

    this._logger.addBreadcrumb({
      message: `${AttachmentService.TAG} addAttachment upload`,
      type: 'debug',
    });

    // Uploading the attachment
    return this._attachmentTransferService.upload(
      { blob, metadata: uploadMetadata, filename, mime },
      uploadMetadata.uniqueId,
      this._connectionStateService.isOnline
    );
  }

  public async uploadLocal(localId: string): Promise<UploadResult | null> {
    this._logger.info(`upload local ${localId}`);
    const isAlreadyUploaded = localId.startsWith(ATTACHMENT_LOCAL_PREFIX) === false;
    if (isAlreadyUploaded) {
      return null;
    }
    const [blob, attachment] = await Promise.all([
      this._attachmentDbProvider.get(Database.Blob, localId),
      this._attachmentDbProvider.get(Database.Attachment, localId),
    ]);

    if (!blob || !attachment) {
      // Self-healing for half states (#26719). Both stores are independent, so either can exist
      // without the other after an interrupted upload or id swap.
      if (attachment) {
        // Orphaned metadata without a blob: nothing can ever be uploaded or resolved for this
        // id again, but the row keeps the document view failing with `blob is missing` on every
        // open. Remove it — when the upload had succeeded before the interruption, the synced
        // server row (same image) is already there and takes over.
        // Note: this branch used to check `if (blob)` and was therefore unreachable in exactly
        // the situation it was written for; the orphans survived every queue run.
        this._logger.error(
          'local attachment found without blob - deleting it',
          new Error('local attachment found without blob - deleting it')
        );
        await this._attachmentDbProvider.remove(Database.Attachment, localId);
      } else if (blob) {
        // Unreferenced blob without metadata: invisible to every reader and to the upload —
        // it would occupy storage forever.
        this._logger.error(
          'local blob found without attachment - deleting it',
          new Error('local blob found without attachment - deleting it')
        );
        await this._attachmentDbProvider.remove(Database.Blob, localId);
      }
      return null;
    }
    const { metadata } = attachment;
    this._logger.info(`upload local ${localId} - uploading`);
    return this._attachmentTransferService.upload(
      { blob, metadata, filename: attachment.filename, mime: attachment.mime },
      localId,
      this._connectionStateService.isOnline
    );
  }

  public async upload(localId: string): Promise<UploadResult | null> {
    return await this.uploadLocal(localId);
  }

  public async update(attachment: AttachmentDto): Promise<boolean> {
    await this._attachmentDbProvider.writeAttachmentToStorage({
      ...attachment,
      uploadDate: new Date(new Date().toUTCString()),
      metadata: { ...attachment.metadata, updatedBy: await this._accountService.getCurrentUserId() },
    });

    await this._attachmentDbProvider.set(Database.AttachmentToUpdate, attachment._id, attachment);
    if (this._connectionStateService.isOnline) {
      await this.runAttachmentQueuesProcessor();
    }
    return true;
  }

  public async softDelete(attachment: AttachmentDto): Promise<boolean> {
    attachment.metadata.archived = true;

    await this._attachmentDbProvider.writeAttachmentToStorage({
      ...attachment,
      uploadDate: new Date(new Date().toUTCString()),
      metadata: { ...attachment.metadata, updatedBy: await this._accountService.getCurrentUserId() },
    });

    await this._attachmentDbProvider.set(Database.AttachmentToUpdate, attachment._id, attachment);

    // Mirror on this device what the next sync round-trip does on every other device once the
    // archived update arrives (WorkItem.sync classifies archived docs as deleted, afterSync drops
    // the blob): remove the synced metadata copy and the local binary right away, so searches
    // (e.g. reopening an audit, patient attachment lists) no longer surface the archived
    // attachment before the sync has caught up.
    //
    // Never for `local|` attachments whose upload is still pending: the queued upload
    // (AttachmentToSave, processed before AttachmentToUpdate) re-keys blob, metadata and the
    // queued archive update to the server id via replaceLocalData/replaceLocalUpdate — that
    // dance needs the local blob and metadata row to survive until the upload has succeeded.
    if (!attachment._id.startsWith(ATTACHMENT_LOCAL_PREFIX)) {
      try {
        await this._attachmentWorkItem.hardDelete(attachment._id);
        await this._attachmentDbProvider.remove(Database.Blob, attachment._id);
      } catch (error) {
        // Local cleanup is best-effort — the sync removes both copies eventually anyway.
        this._logger.error('softDelete: local cleanup failed', error);
      }
    }

    if (this._connectionStateService.isOnline) {
      await this.runAttachmentQueuesProcessor();
    }
    return true;
  }

  public async delete(id: string): Promise<void> {
    if (!this._connectionStateService.isOnline) {
      await this._attachmentDbProvider.set(Database.AttachmentToRemove, id, undefined);
      return;
    }
    try {
      await this.performDelete(id);
    } catch (error) {
      this._logger.error('delete image failed', error);
      await this._attachmentDbProvider.set(Database.AttachmentToRemove, id, undefined);
    }
  }

  private async performDelete(id: string): Promise<void> {
    this._logger.info(`[AttachmentService] Deleting attachment ${id}`);
    const isRemoteAttachment = id.startsWith(ATTACHMENT_LOCAL_PREFIX) === false;
    if (isRemoteAttachment) {
      this._logger.info(`[AttachmentService] Deleting remote attachment ${id}`);
      await this._attachmentTransferService.delete(id);
    }
    await this._attachmentDbProvider.purge(id);
    this._logger.info(`[AttachmentService] Deleted attachment ${id}`);
  }

  private async performUpdate(attachment: AttachmentDto): Promise<void> {
    this._logger.info(`[AttachmentService] Updating attachment ${attachment._id}`);
    const uploadedAttachment = await this._attachmentTransferService.update(attachment._id, attachment);
    await this._attachmentDbProvider.writeAttachmentToStorage(uploadedAttachment);
    await this._attachmentDbProvider.remove(Database.AttachmentToUpdate, attachment._id);
    this._logger.info(`[AttachmentService] Updated attachment ${attachment._id}`);
  }

  /**
   * Reads the attachment *metadata* from the local `attachment.db`. Purely local — the server
   * is never asked for metadata on native; it arrives via the sync pipeline only.
   *
   * Note: the call below is not awaited, so `attachment` is a `Promise` and the `!attachment`
   * guard can never fire — when the metadata is missing this resolves `undefined` instead of
   * throwing the intended error (callers then typically fail with a `TypeError` on property
   * access, see {@link get}).
   */
  public async getAttachmentDto(id: string): Promise<AttachmentDto> {
    const attachment = this._attachmentDbProvider.getAttachmentDto(id);
    if (!attachment) {
      throw Error(`attachment is undefined for ${id}`);
    }
    return attachment;
  }

  public async isAttachmentBlobLocallyAvailable(id: string): Promise<boolean> {
    return this._attachmentDbProvider.isAttachmentBlobAvailable(id);
  }

  public async getLocalAttachments(): Promise<string[]> {
    return this._attachmentDbProvider.getLocalAttachments();
  }

  private registerOnlineObservable(): void {
    this._logger.info('[AttachmentService] Registering online observable');
    try {
      if (this._websocketConnectedSubscription != null && !this._websocketConnectedSubscription.closed) {
        this._websocketConnectedSubscription.unsubscribe();
      }
    } catch (e) {}
    // `online` is exactly "socket connected and authenticated with a valid token"
    this._websocketConnectedSubscription = this._session.state$
      .pipe(
        map(state => state === 'online'),
        distinctUntilChanged(),
        filter(online => online && !this._isBusyWithOfflineUpload),
        debounceTime(2 * 1000), // avoid flickering online -> offline -> online,
        switchMap(async _ => {
          this._isBusyWithOfflineUpload = true;
          try {
            await this.runAttachmentQueuesProcessor();
          } catch (error) {
            this._logger.error('Error on websocketConnectedSubscription', error);
          } finally {
            this._isBusyWithOfflineUpload = false;
          }
        })
      )
      .subscribe(() => {});
  }

  public async runAttachmentQueuesProcessor() {
    const runId = Math.floor(Math.random() * (10000000 - 1 + 1)) + 1;
    this._logger.info(`[AttachmentService-${runId}] Attachment queues processor started`);
    await this._attachmentQueuesProcessor.processQueues(
      this.performDelete.bind(this),
      this.uploadLocal.bind(this),
      this.performUpdate.bind(this)
    );
    this._logger.info(`[AttachmentService-${runId}] Attachment queues processor done`);
  }
}
